import { ArrowDownAZ, ImageUp, Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { mensajeDe } from '@/api/errors';
import { PermissionGate } from '@/auth/PermissionGate';
import { useStepUp } from '@/auth/StepUp';
import { useCan } from '@/auth/queries';
import { Campo } from '@/components/Campo';
import { ConfirmarAccion } from '@/components/ConfirmarAccion';
import { Cargando, ErrorCarga, Vacio } from '@/components/Estados';
import { Modal } from '@/components/Modal';
import { OrdenableLista } from '@/components/OrdenableLista';
import { AvisoSinGuardar, PaginaEditor } from '@/components/PaginaEditor';
import { SubirVarios, type ArchivoSubidoCola } from '@/components/SubirVarios';
import { usePestana } from '@/hooks/usePestana';
import { subirArchivo, validarImagen } from '@/lib/archivos';
import { fechaLarga } from '@/lib/formato';

import { comunesDe, cuerpoComunes, type Comunes } from '../comun/comunes';
import { DatosComunes } from '../comun/DatosComunes';
import { CasosLigados, LicenciasElemento, NotasContenido } from '../comun/Paneles';
import {
  usePresentacion,
  usePresentacionMutaciones,
  type Diapositiva,
  type Presentacion,
} from './queries';

const PESTANAS = [
  ['diapositivas', 'Diapositivas'],
  ['datos', 'Datos'],
  ['licencias', 'Licencias'],
  ['notas', 'Notas'],
  ['casos', 'Casos ligados'],
  ['clases', 'Usada en clases'],
] as const;

type Mut = ReturnType<typeof usePresentacionMutaciones>;

function AgregarDiapositivas({
  p,
  mut,
  abierto,
  onCerrar,
}: {
  p: Presentacion;
  mut: Mut;
  abierto: boolean;
  onCerrar: () => void;
}) {
  const [listos, setListos] = useState<ArchivoSubidoCola[]>([]);
  const [pendientes, setPendientes] = useState(0);
  const [despues, setDespues] = useState<number | null>(null);
  return (
    <Modal
      abierto={abierto}
      onCerrar={onCerrar}
      titulo="Agregar diapositivas"
      descripcion="El nombre de cada archivo queda como etiqueta (sirve para ordenar alfabéticamente)."
      ancho="max-w-3xl"
    >
      <div className="space-y-4">
        <Campo etiqueta="Dónde">
          <select
            className="input-vidrio"
            value={despues ?? ''}
            onChange={(e) => setDespues(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Al final</option>
            {p.slides.map((s, i) => (
              <option key={s.id} value={s.id}>
                Después de la {i + 1}
                {s.label ? ` (${s.label})` : ''}
              </option>
            ))}
          </select>
        </Campo>
        <SubirVarios
          destino="presentacion"
          ownerId={p.id}
          maxNombre={150}
          onCambio={(l, n) => {
            setListos(l);
            setPendientes(n);
          }}
        />
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-cs btn-contorno" onClick={onCerrar}>
            Cancelar
          </button>
          <button
            type="button"
            className="btn-cs btn-primario"
            disabled={listos.length === 0 || pendientes > 0 || mut.agregar.isPending}
            onClick={() =>
              mut.agregar.mutate(
                {
                  afterId: despues,
                  files: listos.map((a) => ({ filePath: a.path, label: a.nombre || null })),
                },
                {
                  onSuccess: () => {
                    toast.success(`${listos.length} diapositiva(s) agregada(s).`);
                    onCerrar();
                  },
                },
              )
            }
          >
            Agregar {listos.length}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function Tarjeta({
  s,
  i,
  asa,
  mut,
  presentacionId,
  editable,
  marcada,
  onMarcar,
}: {
  s: Diapositiva;
  i: number;
  asa: ReactNode;
  mut: Mut;
  presentacionId: number;
  editable: boolean;
  marcada: boolean;
  onMarcar: (v: boolean) => void;
}) {
  const [etiqueta, setEtiqueta] = useState(s.label ?? '');
  const [subiendo, setSubiendo] = useState(false);
  const reemplazar = async (file: File | undefined) => {
    if (!file) return;
    const e = validarImagen(file);
    if (e) return toast.error(e);
    setSubiendo(true);
    try {
      const r = await subirArchivo(file, 'presentacion', presentacionId);
      await mut.editar.mutateAsync({ childId: s.id, body: { filePath: r.path } });
    } catch (err) {
      toast.error(mensajeDe(err));
    } finally {
      setSubiendo(false);
    }
  };
  return (
    <div className="panel-vidrio space-y-2 p-2">
      <div className="flex items-center gap-1 text-xs text-texto-3">
        {asa}
        <span className="font-bold">{i + 1}</span>
        {editable && (
          <input
            type="checkbox"
            className="ml-auto h-4 w-4 accent-[var(--color-azul-600)]"
            aria-label={`Seleccionar diapositiva ${i + 1}`}
            checked={marcada}
            onChange={(e) => onMarcar(e.target.checked)}
          />
        )}
      </div>
      {s.fileUrl ? (
        <a href={s.fileUrl} target="_blank" rel="noreferrer">
          <img
            src={s.fileUrl}
            alt={s.label ?? `Diapositiva ${i + 1}`}
            loading="lazy"
            className="aspect-video w-full rounded bg-black/5 object-contain"
          />
        </a>
      ) : (
        <div className="aspect-video w-full rounded bg-black/5" />
      )}
      <input
        className="input-vidrio py-1 text-xs"
        aria-label={`Etiqueta de la diapositiva ${i + 1}`}
        placeholder="Etiqueta"
        value={etiqueta}
        maxLength={150}
        disabled={!editable}
        onChange={(e) => setEtiqueta(e.target.value)}
        onBlur={() =>
          etiqueta !== (s.label ?? '') &&
          mut.editar.mutate({ childId: s.id, body: { label: etiqueta || null } })
        }
      />
      {editable && (
        <div className="flex gap-1">
          <label className="btn-cs btn-vidrio flex-1 cursor-pointer px-2 py-1 text-xs">
            <ImageUp size={14} aria-hidden /> {subiendo ? 'Subiendo…' : 'Reemplazar'}
            <input
              type="file"
              className="sr-only"
              accept="image/jpeg,image/png,image/webp,image/gif"
              disabled={subiendo}
              onChange={(e) => {
                void reemplazar(e.target.files?.[0]);
                e.target.value = '';
              }}
            />
          </label>
          <button
            type="button"
            className="btn-cs btn-vidrio px-2 py-1 text-rojo-texto"
            aria-label={`Quitar diapositiva ${i + 1}`}
            onClick={() => mut.quitar.mutate([s.id])}
          >
            <Trash2 size={14} aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
}

function Diapositivas({ p, mut }: { p: Presentacion; mut: Mut }) {
  const editable = useCan('contenido', 'editar');
  const [agregando, setAgregando] = useState(false);
  const [marcadas, setMarcadas] = useState<Set<number>>(new Set());
  const [quitando, setQuitando] = useState(false);
  return (
    <div className="space-y-4">
      {editable && (
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" className="btn-cs btn-primario" onClick={() => setAgregando(true)}>
            <Plus size={16} aria-hidden /> Agregar diapositivas
          </button>
          <button
            type="button"
            className="btn-cs btn-vidrio"
            disabled={p.slides.length < 2 || mut.alfabetico.isPending}
            onClick={() => mut.alfabetico.mutate()}
          >
            <ArrowDownAZ size={16} aria-hidden /> Ordenar por etiqueta
          </button>
          {marcadas.size > 0 && (
            <button type="button" className="btn-cs btn-peligro" onClick={() => setQuitando(true)}>
              <Trash2 size={16} aria-hidden /> Quitar {marcadas.size}
            </button>
          )}
          <span className="ml-auto text-sm text-texto-3">
            {p.slides.length} diapositiva(s). Arrastra para ordenar; se guarda al soltar.
          </span>
        </div>
      )}
      {p.slides.length === 0 && <Vacio titulo="Sin diapositivas" />}
      <OrdenableLista
        items={p.slides}
        idDe={(s) => s.id}
        cuadricula
        deshabilitado={!editable || mut.ordenar.isPending}
        className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5"
        onOrdenar={(ids) => mut.ordenar.mutate(ids)}
      >
        {(s, asa, i) => (
          <Tarjeta
            key={`${s.id}-${s.label ?? ''}`}
            s={s}
            i={i}
            asa={asa}
            mut={mut}
            presentacionId={p.id}
            editable={editable}
            marcada={marcadas.has(s.id)}
            onMarcar={(v) =>
              setMarcadas((m) => {
                const n = new Set(m);
                if (v) n.add(s.id);
                else n.delete(s.id);
                return n;
              })
            }
          />
        )}
      </OrdenableLista>
      <AgregarDiapositivas
        key={String(agregando)}
        p={p}
        mut={mut}
        abierto={agregando}
        onCerrar={() => setAgregando(false)}
      />
      <ConfirmarAccion
        abierto={quitando}
        onCerrar={() => setQuitando(false)}
        titulo={`Quitar ${marcadas.size} diapositiva(s)`}
        descripcion="Se quitan de la presentación. Las imágenes del CDN no se borran."
        textoBoton="Quitar"
        peligro
        ocupado={mut.quitar.isPending}
        onConfirmar={() =>
          mut.quitar.mutate([...marcadas], {
            onSuccess: () => {
              setMarcadas(new Set());
              setQuitando(false);
            },
          })
        }
      />
    </div>
  );
}

function Datos({ p, mut }: { p: Presentacion; mut: Mut }) {
  const editable = useCan('contenido', 'editar');
  const inicial = comunesDe(p);
  const [c, setC] = useState<Comunes>(inicial);
  const sucio = JSON.stringify(c) !== JSON.stringify(inicial);
  return (
    <form
      className="panel-vidrio max-w-2xl space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        mut.guardar.mutate(cuerpoComunes(c));
      }}
    >
      <AvisoSinGuardar sucio={sucio} />
      <DatosComunes
        valor={c}
        onCambio={(x) => setC((v) => ({ ...v, ...x }))}
        nombreMax={100}
        deshabilitado={!editable}
      />
      {editable && (
        <div className="flex justify-end">
          <button
            type="submit"
            className="btn-cs btn-primario"
            disabled={!sucio || mut.guardar.isPending}
          >
            Guardar
          </button>
        </div>
      )}
    </form>
  );
}

function Editor({ p }: { p: Presentacion }) {
  const navigate = useNavigate();
  const editable = useCan('contenido', 'editar');
  const [pestana, setPestana] = usePestana(PESTANAS);
  const mut = usePresentacionMutaciones(p.id);
  const [borrando, setBorrando] = useState(false);
  const { proteger, dialogo } = useStepUp();
  return (
    <PaginaEditor
      volver={{ a: '/contenido?type=presentacion', texto: 'Presentaciones' }}
      titulo={p.name}
      descripcion={`Presentación #${p.id}${p.code ? ` · ${p.code}` : ''} · ${p.slides.length} diapositivas${p.updatedAt ? ` · actualizada ${fechaLarga(p.updatedAt)}` : ''}`}
      acciones={
        editable && (
          <button type="button" className="btn-cs btn-vidrio" onClick={() => setBorrando(true)}>
            <Trash2 size={16} aria-hidden /> Borrar
          </button>
        )
      }
      pestanas={PESTANAS}
      pestana={pestana}
      onPestana={setPestana}
    >
      {pestana === 'diapositivas' && <Diapositivas p={p} mut={mut} />}
      {pestana === 'datos' && <Datos key={p.updatedAt} p={p} mut={mut} />}
      {pestana === 'licencias' && (
        <div className="panel-vidrio">
          <LicenciasElemento libraryItemId={p.libraryItemId} asignadas={p.membershipIds} />
        </div>
      )}
      {pestana === 'notas' && (
        <div className="panel-vidrio">
          <NotasContenido destino={{ contentType: 'presentation', contentId: p.id }} />
        </div>
      )}
      {pestana === 'casos' && (
        <div className="panel-vidrio">
          <CasosLigados destino={{ contentType: 'presentation', contentId: p.id }} />
        </div>
      )}
      {pestana === 'clases' && (
        <div className="panel-vidrio">
          {p.classes.length === 0 ? (
            <Vacio titulo="Ninguna clase usa esta presentación" />
          ) : (
            <ul className="space-y-1">
              {p.classes.map((c) => (
                <li key={c.id}>
                  <Link
                    to={`/contenido/clases/${c.id}?tab=presentacion`}
                    className="font-semibold hover:underline"
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <ConfirmarAccion
        abierto={borrando}
        onCerrar={() => setBorrando(false)}
        titulo={`Borrar «${p.name}»`}
        descripcion="Se borra con sus diapositivas y sale de todas las licencias. Si algún alumno la vio o una clase la usa, no se puede borrar."
        textoBoton="Borrar"
        peligro
        ocupado={mut.borrar.isPending}
        onConfirmar={() =>
          void proteger(() => mut.borrar.mutateAsync())
            .then(() => {
              toast.success('Presentación borrada.');
              void navigate('/contenido?type=presentacion');
            })
            .catch((e: unknown) => toast.error(mensajeDe(e)))
        }
      />
      {dialogo}
    </PaginaEditor>
  );
}

export function PresentacionPage() {
  const id = Number(useParams().id);
  const q = usePresentacion(id);
  return (
    <PermissionGate seccion="contenido">
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data && <Editor p={q.data} />}
    </PermissionGate>
  );
}
